/** admin/campaigns — HOOKS. */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { campaignsApi } from './api';
import { adminCampaignsKeys } from './keys';
import type { CreateCampaignPayload, UpdateCampaignPayload } from './types';

const invalidate = (qc: QueryClient) => qc.invalidateQueries({ queryKey: adminCampaignsKeys.all() });

export function useCampaignTemplates(enabled = true) {
  return useQuery({ queryKey: adminCampaignsKeys.templates(), queryFn: () => campaignsApi.listTemplates(), enabled });
}

export function useAdminCampaignsList() {
  return useQuery({ queryKey: adminCampaignsKeys.lists(), queryFn: () => campaignsApi.list() });
}

export function useAdminCampaign(id: string | null) {
  return useQuery({
    queryKey: adminCampaignsKeys.detail(id ?? 'none'),
    queryFn: () => campaignsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: CreateCampaignPayload) => campaignsApi.create(payload), onSuccess: () => invalidate(qc) });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; payload: UpdateCampaignPayload }) => campaignsApi.update(v.id, v.payload), onSuccess: () => invalidate(qc) });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => campaignsApi.remove(id), onSuccess: () => invalidate(qc) });
}

export function useUpdateCampaignChapter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { campaignId: string; chapterId: string; payload: { startTime?: string | null; endTime?: string | null } }) =>
      campaignsApi.updateChapter(v.campaignId, v.chapterId, v.payload),
    onSuccess: () => invalidate(qc),
  });
}
